import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Tab,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import { useWeb3React } from "@web3-react/core";
import { metaMask, metaMaskHooks } from "configs/web3React";
import { signInWithEthereum } from "utils/siweAuth";
import constants from "configs/constants";
import PreviewEligibleProjects from "./PreviewEligibleProjects";
import VotingPeriodTab from "./VotingPeriodTab";

const PinkSaleVotingPeriod = () => {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Web3-react hooks for wallet connection
  const { account, isActive, chainId, connector } = useWeb3React();
  const { useChainId, useAccounts, useIsActivating, useIsActive } =
    metaMaskHooks;
  const chainIdFromHook = useChainId();
  const accounts = useAccounts();
  const isActivating = useIsActivating();
  const isActiveFromHook = useIsActive();

  // Use web3-react state
  const address = account || (accounts && accounts[0]);
  const isConnected = isActive || isActiveFromHook;
  const isConnecting = isActivating;
  const chain = chainId || chainIdFromHook;

  // Auto-connect on mount if previously connected
  useEffect(() => {
    if (metaMask && !isActive && !isActivating) {
      metaMask.connectEagerly().catch(() => {
        // Ignore errors for eager connection
      });
    }
  }, []);

  const handleConnectWallet = async () => {
    try {
      await metaMask.activate();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to connect wallet",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDisconnectWallet = () => {
    if (connector && connector.deactivate) {
      connector.deactivate();
    } else {
      metaMask.deactivate();
    }
    toast({
      title: "Wallet Disconnected",
      description: "Your wallet has been disconnected",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSignInWithEthereum = async () => {
    try {
      setIsSigningIn(true);
      const { token } = await signInWithEthereum();

      localStorage.setItem(constants.STORAGE.PINK_SALE_ACCESS_TOKEN, token);

      toast({
        title: "Sign In Successful",
        description: "You have been signed in with your wallet.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Wallet Sign In Failed",
        description:
          error?.message || "Failed to sign in with wallet. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSigningIn(false);
    }
  };

  // Format address for display
  const formatAddress = (addr: string | undefined) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex
        mb="20px"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
      >
        <HStack spacing={4} mb={{ base: 4, md: 0 }}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Select
            placeholder="All Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            maxW="200px"
          >
            {/* TODO: Add status options */}
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </HStack>
        <HStack spacing={4}>
          {isConnected ? (
            <>
              <Box
                px={4}
                py={2}
                bg="green.50"
                borderRadius="md"
                border="1px solid"
                borderColor="green.200"
              >
                <Text fontSize="sm" color="green.700">
                  {formatAddress(address)}
                </Text>
                {chain && (
                  <Text fontSize="xs" color="green.600">
                    Chain ID: {chain}
                  </Text>
                )}
              </Box>
              <Button
                colorScheme="red"
                size="md"
                onClick={handleDisconnectWallet}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleConnectWallet}
              isLoading={isConnecting}
              loadingText="Connecting..."
            >
              Connect Wallet
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Sign In with Ethereum Button - Show only when connected */}
      {isConnected && (
        <Flex mb="20px" justifyContent="flex-start">
          <Button
            colorScheme="brand"
            size="md"
            onClick={handleSignInWithEthereum}
            isLoading={isSigningIn}
            loadingText="Signing in..."
          >
            Sign In with Ethereum
          </Button>
        </Flex>
      )}

      <Tabs variant="enclosed" mt="20px">
        <TabList>
          <Tab>Voting Periods</Tab>
          <Tab>Preview Eligible Projects</Tab>
        </TabList>

        <TabPanels>
          {/* Voting Periods Tab */}
          <TabPanel>
            <VotingPeriodTab page={page} onPageChange={handlePageChange} />
          </TabPanel>

          {/* Preview Eligible Projects Tab */}
          <TabPanel>
            <PreviewEligibleProjects />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default PinkSaleVotingPeriod;
